import { useNavigate } from 'react-router-dom';
import { Bird, Cat, ChevronRight, Dog, Fish, PawPrint, Plus, Rabbit } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState } from '@/components/ui/empty-state';
import { ErrorState } from '@/components/ui/error-state';
import { SectionHeader } from '@/components/ui/section-header';
import { AuthImage } from '@/components/auth-image';
import { use_pets } from '@/features/pet/hooks';
import { SPECIES_OPTIONS, type Pet } from '@/features/pet/types';

const SPECIES_ICON: Record<Pet['species'], LucideIcon> = {
  DOG: Dog,
  CAT: Cat,
  BIRD: Bird,
  RABBIT: Rabbit,
  FISH: Fish,
  OTHER: PawPrint,
};

function species_label(s: Pet['species']): string {
  return SPECIES_OPTIONS.find((o) => o.value === s)?.label ?? s;
}

/** Friendly age from a birth date or a months count (whichever is set). */
function age_label(p: Pet): string | null {
  let months: number | null = null;
  if (p.birth_date) {
    const b = new Date(p.birth_date + 'T00:00:00');
    const now = new Date();
    months = (now.getFullYear() - b.getFullYear()) * 12 + (now.getMonth() - b.getMonth());
    if (now.getDate() < b.getDate()) months -= 1;
  } else if (p.approximate_age_months !== null) {
    months = p.approximate_age_months;
  }
  if (months === null || months < 0) return null;
  const y = Math.floor(months / 12);
  const m = months % 12;
  if (y === 0) return `${m} mo`;
  if (m === 0) return `${y} yr`;
  return `${y} yr ${m} mo`;
}

function Chip({ children }: { children: React.ReactNode }) {
  return (
    <span className="inline-flex items-center rounded-full bg-secondary px-2 py-0.5 text-[11px] font-semibold text-secondary-foreground">
      {children}
    </span>
  );
}

function PetCard({ pet, onClick }: { pet: Pet; onClick: () => void }) {
  const Icon = SPECIES_ICON[pet.species] ?? PawPrint;
  const age = age_label(pet);
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`pet-card-${pet.id}`}
      className="group clay-press flex w-full items-center gap-4 rounded-card border-2 border-brand-outline-variant/20 bg-card p-4 text-left shadow-clay transition-all duration-normal ease-bounce hover:-translate-y-1 hover:border-primary/40 hover:shadow-clay-lg"
    >
      {pet.primary_photo_url ? (
        <AuthImage
          path={`${pet.primary_photo_url}?variant=thumb`}
          alt={pet.name}
          className="h-16 w-16 shrink-0 rounded-2xl object-cover shadow-clay-sm"
        />
      ) : (
        <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl accent-gradient text-white shadow-clay-sm">
          <Icon className="h-8 w-8" />
        </div>
      )}
      <div className="min-w-0 flex-1">
        <p className="truncate font-heading text-lg font-bold leading-tight">{pet.name}</p>
        <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
          <Chip>{species_label(pet.species)}</Chip>
          {age && <Chip>{age}</Chip>}
          {pet.weight_kg !== null && <Chip>{pet.weight_kg} kg</Chip>}
          {pet.sex !== 'UNKNOWN' && <Chip>{pet.sex === 'MALE' ? 'Male' : 'Female'}</Chip>}
        </div>
      </div>
      <ChevronRight className="h-5 w-5 shrink-0 text-muted-foreground transition-transform duration-fast group-hover:translate-x-0.5" />
    </button>
  );
}

export function PetsListPage() {
  const navigate = useNavigate();
  const pets_q = use_pets();
  const pets = pets_q.data ?? [];

  return (
    <div className="flex flex-col gap-5">
      <SectionHeader
        title="My Pets"
        description={
          pets.length > 0
            ? `${pets.length} ${pets.length === 1 ? 'profile' : 'profiles'} you can reuse when booking.`
            : 'Profiles you can reuse when booking any service.'
        }
        action={
          <Button variant="gradient" onClick={() => navigate('/app/pets/new')} data-testid="add-pet">
            <Plus className="h-4 w-4" /> Add a pet
          </Button>
        }
      />

      {pets_q.isLoading && (
        <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="pets-loading">
          {Array.from({ length: 4 }).map((_, i) => (
            <li key={i}>
              <Skeleton className="h-[104px] w-full rounded-card" />
            </li>
          ))}
        </ul>
      )}

      {pets_q.isError && (
        <ErrorState
          data-testid="pets-error"
          title="Couldn't load your pets"
          description="Please try again."
        />
      )}

      {pets_q.data?.length === 0 && (
        <EmptyState
          data-testid="pets-empty"
          icon={PawPrint}
          title="No pets yet"
          description="Add your first pet — it only takes a minute, and you can reuse the profile every time you book."
          action={
            <Button variant="gradient" onClick={() => navigate('/app/pets/new')}>
              <Plus className="h-4 w-4" /> Add a pet
            </Button>
          }
        />
      )}

      {pets.length > 0 && (
        <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {pets.map((p) => (
            <li key={p.id}>
              <PetCard pet={p} onClick={() => navigate(`/app/pets/${p.id}`)} />
            </li>
          ))}
          <li>
            <button
              type="button"
              onClick={() => navigate('/app/pets/new')}
              data-testid="add-pet-tile"
              className="clay-press flex h-full min-h-[104px] w-full flex-col items-center justify-center gap-1.5 rounded-card border-2 border-dashed border-brand-outline-variant/40 bg-secondary/30 p-4 text-muted-foreground transition-colors duration-fast hover:border-primary/50 hover:text-primary"
            >
              <span className="flex h-10 w-10 items-center justify-center rounded-full bg-card text-primary shadow-clay-sm">
                <Plus className="h-5 w-5" />
              </span>
              <span className="text-sm font-semibold">Add a pet</span>
            </button>
          </li>
        </ul>
      )}
    </div>
  );
}
